import { apiGet, apiPost, apiPut } from '@/utils/api';
import getPath from '@/utils/getPath';
import { handleApiError } from '@/utils/errorHandler';

export const userApi = {
  /**
   * Fetches all users, optionally filtered by is_active status.
   * @apiRoute GET '/users'
   */
  async getAllUsers(is_active = null) {
    try {
      const params =
        is_active !== null && is_active !== undefined
          ? new URLSearchParams({ is_active })
          : null;
      const response = await apiGet(getPath('users'), params);
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getAllUsers');
    }
  },

  /**
   * Creates a new user.
   * @apiRoute POST '/users'
   */
  async createUser(payload) {
    try {
      const response = await apiPost(getPath('users'), payload);
      return response.data;
    } catch (err) {
      await handleApiError(err, 'createUser', true, 'Could not create user');
    }
  },

  /**
   * Updates a user by auxo_user_id.
   * @apiRoute PUT '/users/<auxo_user_id>'
   */
  async updateUser(auxo_user_id, payload) {
    try {
      const response = await apiPut(getPath(`users/${auxo_user_id}`), payload);
      return response.data;
    } catch (err) {
      await handleApiError(err, 'updateUser', true, 'Could not update user');
    }
  },

  /**
   * Uploads a user avatar image.
   * @apiRoute PUT '/users/avatar'
   */
  async uploadAvatar(imageData, headers) {
    try {
      const response = await apiPut(
        getPath('users/avatar'),
        { imageData },
        headers
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'uploadAvatar', true, 'Avatar upload failed');
    }
  },

  /**
   * Fetches a user by email.
   * @apiRoute GET '/users'
   */
  async getUserByEmail(email) {
    try {
      const response = await apiGet(
        getPath(`users?email=${email}&expect_single=true`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getUserByEmail');
    }
  },

  /**
   * Fetches a user by auxo_user_id.
   * @apiRoute GET '/users/<auxo_user_id>'
   */
  async getUserById(auxo_user_id) {
    try {
      const response = await apiGet(getPath(`users/${auxo_user_id}`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getUserById');
    }
  },

  /**
   * Sets the user goal for a user.
   * @apiRoute PUT '/users/<auxo_user_id>/goal'
   */
  async setUserGoal(auxo_user_id, goal) {
    try {
      const response = await apiPut(getPath(`users/${auxo_user_id}/goal`), {
        value: goal,
      });
      return response.data;
    } catch (err) {
      await handleApiError(err, 'setUserGoal');
    }
  },

  /**
   * Tracks user auth activity.
   * @apiRoute POST '/users/auth_activity'
   */
  async authActivity(action) {
    try {
      const response = await apiPost(
        getPath(`users/auth_activity?action=${action}`)
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'authActivity');
    }
  },

  /**
   * Fetches worklists assigned to a user.
   * @apiRoute GET '/users/<auxo_user_id>/worklists'
   */
  async getUserWorklists(auxo_user_id) {
    try {
      const response = await apiGet(getPath(`users/${auxo_user_id}/worklists`));
      return response.data;
    } catch (err) {
      await handleApiError(err, 'getUserWorklists');
    }
  },

  /**
   * Sets shift duration for a user.
   * @apiRoute PUT '/users/<auxo_user_id>/shift_duration'
   */
  async setUserShiftDuration(auxo_user_id, shift_duration) {
    try {
      const response = await apiPut(
        getPath(`users/${auxo_user_id}/shift_duration`),
        { shift_duration }
      );
      return response.data;
    } catch (err) {
      await handleApiError(err, 'setUserShiftDuration');
    }
  },
};
